import type { Campaign } from '~/modules/campaign/types';
import type { CampaignStatus } from '~/types/graphql';

import { useCampaignUpdateMutation } from '~/api/graphql/mutations/campaignUpdate.generated';
import { useTrackingAnalytic } from '~/modules/tracking/hooks/useTrackingAnalytic';
import { useTrackingSlack } from '~/modules/tracking/hooks/useTrackingSlack';
import { useCampaignStore } from '~/modules/campaign';
import { EVENT_ANALYTIC } from '~/modules/tracking';
import { useToast } from '~/hooks/useToast';
import { CAMPAIGN_MSG } from '../constants';
import { useState } from 'react';

export interface UpdateCampaignStateOptions {
  hideSuccessMessage?: boolean;
}

export interface IUpdateCampaignState<T extends Campaign> {
  isLoadingUpdateState: boolean;
  onUpdateCampaignState: (
    campaign: T,
    options?: UpdateCampaignStateOptions,
  ) => Promise<T | undefined>;
}

export const useUpdateCampaignState = <T extends Campaign>(): IUpdateCampaignState<T> => {
  const [isLoading, setIsLoading] = useState(false);

  const upsertCampaign = useCampaignStore((state) => state.upsertCampaign);
  const getCampaignById = useCampaignStore((state) => state.getCampaignById);
  const updateCampaignMutation = useCampaignUpdateMutation();

  const { shopifyToast } = useToast();
  const { handleTrackingCampaign } = useTrackingSlack();
  const { trackEvent } = useTrackingAnalytic();

  const handleToastMessage = (
    status: CampaignStatus,
    isSuccess = false,
    hideSuccessMessage = false,
  ) => {
    if (hideSuccessMessage) return;

    const toastMessages: Record<CampaignStatus, any> = {
      COMPLETED: CAMPAIGN_MSG.RUNNING,
      RUNNING: CAMPAIGN_MSG.RUNNING,
      PAUSED: CAMPAIGN_MSG.PAUSED,
      STOPPED: CAMPAIGN_MSG.ENDED,
      DRAFT: CAMPAIGN_MSG.PAUSED,
    };

    const message = isSuccess ? toastMessages[status].SUCCESS : toastMessages[status].FAILED;
    shopifyToast(message, { isError: !isSuccess });
  };

  const onUpdateCampaignState = async (
    campaign: T,
    options?: UpdateCampaignStateOptions,
  ): Promise<T | undefined> => {
    if (!campaign) return;
    setIsLoading(true);

    const { hideSuccessMessage = false } = options || {};

    try {
      const { campaignUpdate } = await updateCampaignMutation.mutateAsync({
        id: campaign.id,
        input: {
          status: campaign.status,
        },
      });
      if (!campaignUpdate) throw new Error('Campaign update failed');

      // if (campaign.type === CAMPAIGN_TYPE.TEMPLATE) {
      //   await handleSyncToLivePage();
      // }

      handleToastMessage(campaign.status!, true, hideSuccessMessage);
      upsertCampaign(campaignUpdate);

      try {
        handleTrackingCampaign(campaign);
        trackEvent(EVENT_ANALYTIC.GEMX_CAMPAIGN_STATUS, {
          campaign: {
            id: campaign.id,
            status: campaign.status,
          },
        });
      } catch (_error) {}
      return campaignUpdate as T;
    } catch (error) {
      const currentCampaign = getCampaignById(campaign?.id);
      if (currentCampaign) {
        await updateCampaignMutation.mutateAsync({
          id: currentCampaign?.id,
          input: {
            status: currentCampaign?.status,
          },
        });
      }

      handleToastMessage(campaign.status!, false);
      throw new Error((error as Error).message);
    } finally {
      setIsLoading(false);
    }
  };

  return {
    isLoadingUpdateState: isLoading,
    onUpdateCampaignState,
  };
};
